import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Property } from "@/hooks/useProperties";
import { useTenant } from "@/hooks/useTenant";
import { copyVarsForTenant } from "@/hooks/useSiteCopy";
import { filterProperties, type BrowseContext } from "@/lib/browseFilters";
import { collectionCopy, collectionPath, collectionSearch, type Collection } from "@/lib/collections";

interface Props {
  collections: readonly Collection[];
  properties: readonly Property[];
  ctx: BrowseContext;
  /** Count and link within one area (the area page, an area child). */
  area?: { slug: string; name: string } | null;
  /** Leave one out (the collection page linking to its siblings). */
  excludeSlug?: string;
  variant?: "chips" | "cards";
  /** Hide collections with no matching home; the index keeps them (they show an empty state). */
  hideEmpty?: boolean;
  heading?: string;
  className?: string;
}

/** Links to the collections that have homes: "Browse by need" on the home, area, browse and collection pages. */
const CollectionLinks = ({
  collections,
  properties,
  ctx,
  area = null,
  excludeSlug,
  variant = "chips",
  hideEmpty = true,
  heading,
  className,
}: Props) => {
  const tenant = useTenant();
  const vars = useMemo(() => copyVarsForTenant(tenant), [tenant]);
  const rows = useMemo(
    () =>
      collections
        .filter((c) => c.slug !== excludeSlug)
        .map((c) => ({
          collection: c,
          copy: collectionCopy(c, vars, area),
          count: filterProperties(properties, collectionSearch(c, ctx, area?.slug), ctx).length,
        }))
        .filter((r) => !hideEmpty || r.count > 0),
    [collections, excludeSlug, vars, area, properties, ctx, hideEmpty],
  );
  if (rows.length === 0) return null;

  if (variant === "cards") {
    return (
      <div className={className}>
        {heading && <h2 className="font-serif text-2xl font-semibold text-foreground mb-5">{heading}</h2>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(({ collection, copy, count }) => (
            <Link
              key={collection.slug}
              to={collectionPath(collection.slug, area?.slug)}
              className="group flex flex-col rounded-card border border-border bg-card p-5 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{copy.title}</h3>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-1" />
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 flex-1">{copy.intro}</p>
              <p className="mt-3 text-[13px] font-medium text-primary">
                {count} {count === 1 ? "home" : "homes"}
              </p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {heading && <span className="text-[13px] text-muted-foreground mr-1">{heading}</span>}
      {rows.map(({ collection, copy, count }) => (
        <Link
          key={collection.slug}
          to={collectionPath(collection.slug, area?.slug)}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[13px] text-foreground hover:border-primary/50 hover:text-primary transition-colors"
        >
          {copy.title}
          <span className="text-muted-foreground">{count}</span>
        </Link>
      ))}
    </div>
  );
};

export default CollectionLinks;
